// House-number / extension coherence.
//
// In the DMP address model the `house_number` is the numeric part of a house number (e.g. "7") and the
// `house_number_extension` is the full alphanumeric house number (e.g. "7a", or simply "7" when there is
// no suffix letter). The two are COHERENT when the extension's leading digits equal the house number:
//
//     "7"  + "7a"  → ok          "51" + "51"  → ok          "12" + "12B" → ok
//     "7"  + "8a"  → NOT ok      "7"  + "70"  → NOT ok       "12" + "1B"  → NOT ok
//     "3"  + ""    → NOT ok (a house number with a blank extension)
//
// A blank house number defers to the required-field checks (treated as coherent here). But a house number
// WITH a blank extension is INCOHERENT — the extension is the full house number (e.g. "3" → "3" or "3a"),
// so it must be filled, not empty. An extension that carries no leading digit (e.g. "a") is left to other
// rules and treated as coherent. The comparison is numeric, so cosmetic leading zeros agree ("07" ≡ "7").
// Shared by the create/validation/mapping forms (client) and their zod schemas + save guard (server).
export function houseNumberExtensionCoherent(houseNumber, extension) {
    const hn = (houseNumber ?? '').trim();
    const ext = (extension ?? '').trim();
    // No house number → defer to the required-field checks (nothing to compare yet).
    if (hn === '')
        return true;
    // House number present but the extension is blank → incoherent: the extension must repeat the house number.
    if (ext === '')
        return false;
    const extDigits = ext.match(/^\d+/);
    const hnDigits = hn.match(/^\d+/);
    // Neither side exposes a numeric part to compare (e.g. a purely alphabetic value) → defer to other rules.
    if (!extDigits || !hnDigits)
        return true;
    return parseInt(extDigits[0], 10) === parseInt(hnDigits[0], 10);
}
/**
 * DISPLAY rule (user ask 2026-09-14): the pair is shown in RED when the extension does not start with
 * the house number — « 5 » + « 5 » ok, « 5 » + « 5A » ok, « 5 » + « A » NOT ok.
 *
 * ⚠️ This is deliberately NOT houseNumberExtensionCoherent, and the two must not be merged. That one
 * guards INPUT — it is wired into the create/validation/mapping forms, their zod schemas, the server
 * save guard, and it DISABLES the Save button. This one only paints data that already exists in the
 * DMP, which we did not create: user decision of 2026-09-14 is « rouge seul, jamais bloquant », so
 * tightening the shared predicate would have silently frozen tickets on somebody else's defect.
 *
 * The single behavioural difference is the case the user named: an extension with NO leading digit
 * (« A »). houseNumberExtensionCoherent defers it to other rules and returns coherent; here it is a
 * mismatch. Everything else agrees, empty extension included.
 *
 * A house number that is not numeric at all (« A » + « A ») is left alone — that is the separate
 * houseNumberDigitsOnly rule, and flagging it here would paint the same defect twice.
 */
export function houseNumberExtensionMismatch(houseNumber, extension) {
    const hn = (houseNumber ?? '').trim();
    const ext = (extension ?? '').trim();
    if (hn === '')
        return false;
    const hnDigits = hn.match(/^\d+/);
    if (!hnDigits)
        return false;
    if (ext === '')
        return true;
    const extDigits = ext.match(/^\d+/);
    if (!extDigits)
        return true;
    return parseInt(extDigits[0], 10) !== parseInt(hnDigits[0], 10);
}
/** Create-form rule (user decision 2026-07-31 — dup case Chaussée de Bruxelles 7C, jvid 13500626 vs
 *  13500633: a "7C" typed INTO the number slipped past every guard): a house NUMBER is digits only —
 *  any letter belongs in the extension (n° 7 + ext 7C). Blank defers to the required-field checks.
 *  Shared by the create form (live hint + submit) and its zod schema (hard server block). */
export function houseNumberDigitsOnly(houseNumber) {
    const hn = (houseNumber ?? '').trim();
    if (hn === '')
        return true;
    return /^\d+$/.test(hn);
}
/**
 * Parse a raw `hnr_full` value from the Appeee MDU-request table — which packs the extension into the house
 * number ("35A", "19  Bis", "167B") — into the DMP address model's two parts (see houseNumberExtensionCoherent):
 *   - `houseNumber`: the leading numeric part ("35", "19", "167"); '' when the value has no leading digits.
 *   - `extension`: the FULL alphanumeric token ("35A", "19 Bis", "167B") — the DMP `house_number_extension`.
 * Inner whitespace is collapsed ("19  Bis" → "19 Bis") and the value is trimmed. Used to (a) match a request
 * against public.address on the numeric house number and (b) pre-fill the Create-address form (Cas 3).
 */
export function parseHnrFull(hnrFull) {
    const full = (hnrFull ?? '').trim().replace(/\s+/g, ' ');
    const m = full.match(/^\d+/);
    return { houseNumber: m ? m[0] : '', extension: full };
}
//# sourceMappingURL=house-number.js.map